import { Suspense, lazy } from 'react';

import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { ViewSwitcher } from '@/features/work-items/views/ViewSwitcher';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';

const GanttView = lazy(() =>
  import('@/features/work-items/views/GanttView').then((m) => ({ default: m.GanttView })),
);

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug/gantt')({
  component: GanttPage,
});

function GanttPage() {
  const { wsSlug, projectSlug } = useParams({
    from: '/_app/$wsSlug/projects/$projectSlug/gantt',
  });
  const navigate = useNavigate();

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projQ = useQuery({
    ...getProjectBySlugQuery(wsQ.data?.id ?? '', projectSlug),
    enabled: !!wsQ.data?.id,
  });

  if (wsQ.isPending || (wsQ.data && projQ.isPending)) {
    return <div className="p-6 text-sm text-quiet">Cargando proyecto...</div>;
  }
  if (!wsQ.data || !projQ.data) {
    return (
      <div className="p-6 text-sm text-quiet">
        Proyecto no encontrado o sin permisos para verlo.
      </div>
    );
  }
  const project = projQ.data;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-border-soft">
        <div>
          <h2 className="text-lg font-semibold text-ink">{project.name}</h2>
          <p className="text-sm text-quiet mt-0.5">
            Vista de cronograma con dependencias. Cada barra es una tarea con su rango de fechas.
            Útil para planificación.
          </p>
        </div>
        <ViewSwitcher
          value="gantt"
          onChange={(next) => {
            if (next === 'kanban') {
              navigate({
                to: '/$wsSlug/projects/$projectSlug/board',
                params: { wsSlug, projectSlug },
              });
            } else if (next === 'list') {
              navigate({
                to: '/$wsSlug/projects/$projectSlug/list',
                params: { wsSlug, projectSlug },
              });
            }
          }}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <Suspense fallback={<div className="p-8 text-center text-quiet">Cargando Gantt...</div>}>
          <GanttView projectId={project.id} workspaceSlug={wsSlug} projectSlug={projectSlug} />
        </Suspense>
      </div>
    </div>
  );
}
